import type { Viewer } from '../../Viewer';
import type { ContextMenuOpenEventPayload } from '../ContextMenuController';

/**
 * 右键删除处理基类。
 *
 * 设计说明：
 * 1. 统一删除执行模板，避免各模式重复编写“前置校验 + 渲染刷新”；
 * 2. 具体删除细节由子类实现，后续扩展其他模式时仅需继承本基类；
 * 3. 基类只依赖 Viewer 抽象门面，不依赖具体草图对象类型。
 */
export abstract class AbstractContextMenuDeleteHandler {
    /** Viewer 实例。 */
    protected readonly viewer: Viewer;

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     */
    protected constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 执行删除动作模板。
     *
     * @param context 菜单上下文快照。
     * @returns 删除动作是否执行成功。
     */
    public execute(context: ContextMenuOpenEventPayload): boolean {
        if (!this.canExecute(context)) {
            return false;
        }

        const removed = this.executeInternal(context);
        if (!removed) {
            return false;
        }

        // 删除与选择清理已在当前调用栈完成，登记普通重绘即可，不在菜单事件里强制提交管线。
        this.viewer.clearSelectionProgrammatically();
        this.viewer.requestRender();
        return true;
    }

    /**
     * 删除前置校验。
     *
     * @param context 菜单上下文快照。
     * @returns 是否允许执行删除。
     */
    protected canExecute(context: ContextMenuOpenEventPayload): boolean {
        return context.selectedObjects.length > 0
            || context.selectedSketchDimensionIds.length > 0;
    }

    /**
     * 子类实现具体删除逻辑。
     *
     * @param context 菜单上下文快照。
     * @returns 删除动作是否执行成功。
     */
    protected abstract executeInternal(context: ContextMenuOpenEventPayload): boolean;
}
